import { useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, translateText } from "../firebase";
import { LANGUAGES } from "../theme";

const ALL_LANG_CODES = Object.keys(LANGUAGES);

/**
 * Scans the user's own messages across all their conversations for docs where
 * `translationPending == true` (i.e. sent while offline / when translate() was
 * unreachable), calls translate(), and fills in the translations map.
 *
 * Fires on: initial mount (once conversations load), window 'online', window 'focus'.
 * A short cooldown prevents redundant scans when multiple events fire in a burst.
 */
export function usePendingTranslationRetry(uid, conversations) {
  const lastRunRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!uid || !conversations || conversations.length === 0) return;

    const COOLDOWN_MS = 5000;

    const scan = async () => {
      const now = Date.now();
      if (inFlightRef.current) return;
      if (now - lastRunRef.current < COOLDOWN_MS) return;
      inFlightRef.current = true;
      lastRunRef.current = now;

      try {
        for (const convo of conversations) {
          const q = query(
            collection(db, "conversations", convo.id, "messages"),
            where("senderUid", "==", uid),
            where("translationPending", "==", true)
          );
          let snap;
          try {
            snap = await getDocs(q);
          } catch (err) {
            // Offline: getDocs may serve from cache and still succeed;
            // if it fails outright, move on and retry next trigger.
            console.debug("Pending-translation scan skipped:", convo.id, err?.message);
            continue;
          }

          for (const d of snap.docs) {
            const msg = d.data();
            if (!msg.originalText) {
              // Nothing to translate (e.g. photo/voice); just clear the flag.
              try {
                await updateDoc(doc(db, "conversations", convo.id, "messages", d.id), {
                  translationPending: false,
                });
              } catch (_) {}
              continue;
            }

            const senderLang = msg.senderLang;
            const targetLangs = ALL_LANG_CODES.filter((c) => c !== senderLang);
            if (targetLangs.length === 0) {
              try {
                await updateDoc(doc(db, "conversations", convo.id, "messages", d.id), {
                  translationPending: false,
                });
              } catch (_) {}
              continue;
            }

            try {
              const { data } = await translateText({
                text: msg.originalText,
                targetLangs,
              });
              const translations = data.translations || {};
              await updateDoc(doc(db, "conversations", convo.id, "messages", d.id), {
                translations,
                translatedText: Object.values(translations)[0] || msg.originalText,
                translationPending: false,
              });
            } catch (err) {
              // Still offline or translate() error — leave the flag set for a
              // future retry pass. Do NOT clear the flag.
              console.warn("Pending translation retry failed for", d.id, err);
            }
          }
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    // Initial scan on mount
    scan();

    const onOnline = () => scan();
    const onFocus = () => {
      if (navigator.onLine) scan();
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
    };
  }, [uid, conversations]);
}
